import { useCallback, useEffect, useState } from "react";
import {
  useListContext,
  useRefresh,
  useUpdate,
  Datagrid,
  TextField,
  FunctionField,
  DateField,
  Loading,
  useNotify,
} from "react-admin";
import {
  Tooltip,
  Box,
  Typography,
  CircularProgress,
  Checkbox,
  IconButton,
  Link,
  Link as MuiLink,
  TextField as MuiTextField,
} from "@mui/material";
import CakeIcon from "@mui/icons-material/Cake";
import EventIcon from "@mui/icons-material/Event";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useIntersectionObserver } from "./useIntersectionObserver";
import debounce from "just-debounce-it";
import { Article, Description } from "@mui/icons-material";
import { useNotification } from "../../../NotificationContext";
import moment from "moment";

const WhatsAppField = ({ record }) => {
  if (!record || !record.phone) return null;
  return (
    <MuiLink
      href={`https://wa.me/${record.countryCode}${record.phone}`}
      target="_blank"
      rel="noreferrer"
      color="primary"
    >
      {record.phone}
    </MuiLink>
    // <a href={`https://wa.me/${record.countryCode}${record.phone}`} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "black" }}>
    //   {record.phone}
    // </a>
  );
};

const ServicesButton = ({ record }) => {
  return (
    <Link href={"#/enterprise/customers/" + record.id + "/services"} style={{ display: "flex", alignItems: "center" }}>
      <IconButton size="medium" color="primary">
        <Article fontSize="medium" />
      </IconButton>
    </Link>
  );
};

const FormsButton = ({ record }) => {
  return (
    <Link href={"#/enterprise/customers/" + record.id + "/forms"} style={{ display: "flex", alignItems: "center" }}>
      <IconButton size="medium" color="primary">
        <Description fontSize="medium" />
      </IconButton>
    </Link>
  );
};

const InfiniteListLayout = ({ setTotalCount, setTotalPages, isRecordatorio, plan }) => {
  const refresh = useRefresh();
  const { data, total, page, setPage, isLoading, hasNextPage, sort } = useListContext();
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, []);
  const [details, setDetails] = useState({});
  const [allRecords, setAllRecords] = useState([]);
  const [historial, setHistorial] = useState({});
  const [update] = useUpdate();
  const notify = useNotify();
  const profile = JSON.parse(localStorage.getItem("profile"));
  useEffect(() => {
    if (!data) return;
    if (page === 1) {
      setAllRecords(data);
    } else {
      setAllRecords((prev) => {
        const existingIds = new Set(prev.map((r) => r.id));
        const newRecords = data.filter((r) => !existingIds.has(r.id));
        const combined = [...prev, ...newRecords];
        return combined;
      });
    }
    setTotalCount(total);
  }, [page, data]);

  setTotalPages(allRecords.length);
  const canLoadMore = allRecords.length < total;
  const [sentinelRef, isIntersecting] = useIntersectionObserver({
    rootMargin: "0px",
    threshold: 0.1,
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceHandleNextPage = useCallback(
    debounce(() => setPage(page + 1), 1000),
    [setPage]
  );
  useEffect(() => {
    if (isIntersecting && canLoadMore && hasNextPage) {
      debounceHandleNextPage();
    }
  }, [isIntersecting, canLoadMore, debounceHandleNextPage, hasNextPage]);

  const handleDetailChange = (id, value) => {
    setDetails((prev) => ({ ...prev, [id]: value }));
  };

  const handleDetailBlur = async (record) => {
    const detail = details[record._id];
    if (!detail) return;
    const customer = allRecords.find((c) => c._id === record._id);
    if (!customer) return;
    const updatedCustomer = { ...customer, detail };

    if (updatedCustomer.countryCode === undefined) {
      updatedCustomer.countryCode = "591";
    }
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${process.env.REACT_APP_API_URL}/enterprise/customers/${record._id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(updatedCustomer),
      });

      if (!response.ok) {
        throw new Error("Error al actualizar el cliente");
      }
      console.log(`Cliente ${record._id} actualizado con detalle:`, detail);
    } catch (error) {
      console.error("Error al actualizar detalle del cliente:", error);
    }
  };

  const { addNotification } = useNotification();

  useEffect(() => {
    if (!allRecords.length) return;

    const today = moment().format("YYYY-MM-DD");

    const stored = JSON.parse(localStorage.getItem("birthdaysShown") || "{}");

    if (stored.date !== today) {
      stored.date = today;
      stored.ids = [];
    }

    const updatedIds = [...stored.ids];

    data.forEach((customer) => {
      if (!customer.birthdate) return;

      const birthdate = moment.utc(customer.birthdate);
      const today = moment();
      const tomorrow = moment().add(1, "day");

      const birthdayThisYear = moment().month(birthdate.month()).date(birthdate.date());

      const isToday = birthdayThisYear.isSame(today, "day");
      const isTomorrow = birthdayThisYear.isSame(tomorrow, "day");

      if (isToday || isTomorrow) {
        const id = `birthday-${customer.id}-${isToday ? "today" : "tomorrow"}`;
        const idByName = `birthday-${customer.fullName}-${isToday ? "today" : "tomorrow"}`;

        if (!updatedIds.includes(id) && !updatedIds.includes(idByName)) {
          addNotification({
            id,
            title: "🎉 Cumpleaños",
            message: `${isToday ? "Hoy" : "Mañana"} cumple años ${customer.fullName}`,
          });
          updatedIds.push(id);
        }
      }
    });

    localStorage.setItem("birthdaysShown", JSON.stringify({ date: today, ids: updatedIds }));

    localStorage.setItem("customers", JSON.stringify(allRecords));

    const customersString = JSON.stringify(allRecords.map((c) => c.id));

    if (customersString === sessionStorage.getItem("prevCustomers")) return;
    sessionStorage.setItem("prevCustomers", customersString);

    const fetchHistorial = async () => {
      const token = localStorage.getItem("token");
      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/booking/customer/historial${profile.branch === undefined ? "" : `?branch._id=${profile.branch._id}`}`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ customers: allRecords }),
          }
        );

        if (!response.ok) {
          throw new Error("Error al obtener historial");
        }

        const result = await response.json();
        const historialMap = result.data.reduce((acc, customer) => {
          acc[customer.id] = customer.historial.length;
          return acc;
        }, {});

        setHistorial(historialMap);
      } catch (error) {
        console.error("Error al obtener historial de reservas:", error);
      }
    };

    fetchHistorial();
  }, [allRecords, addNotification]);

  useEffect(() => {
    if (!sort || !sort.field) return;

    setAllRecords((prev) => {
      const sorted = [...prev].sort((a, b) => {
        let aValue, bValue;

        if (sort.field === "daysSinceLastService") {
          aValue = a.lastBooking?.bookingDate ? moment().diff(moment(a.lastBooking.bookingDate), "days") : 0;
          bValue = b.lastBooking?.bookingDate ? moment().diff(moment(b.lastBooking.bookingDate), "days") : 0;
        } else if (sort.field === "historial") {
          aValue = historial[a.id] ?? 0;
          bValue = historial[b.id] ?? 0;
        } else if (sort.field === "birthday") {
          const today = moment();

          const getDaysUntil = (birthdate) => {
            if (!birthdate) return 9999;
            const bday = moment.utc(birthdate);
            const next = moment().month(bday.month()).date(bday.date());
            if (next.isBefore(today, "day")) next.add(1, "year");
            return next.diff(today, "days");
          };

          aValue = getDaysUntil(a.birthdate);
          bValue = getDaysUntil(b.birthdate);
        } else {
          aValue = a[sort.field] ?? 0;
          bValue = b[sort.field] ?? 0;
        }

        return sort.order === "ASC" ? aValue - bValue : bValue - aValue;
      });

      return sorted;
    });
  }, [sort, historial]);

  const handleUpdateReminder = (e, record) => {
    const customerId = record._id;

    const reminderEnabledUpdate = !record.reminderEnabled;

    update(
      "enterprise/customers",
      {
        id: customerId,
        data: { ...record, reminderEnabled: reminderEnabledUpdate },
        previousData: record,
      },
      {
        onSuccess: () => {
          notify("Recordatorio actualizado", { type: "info" });
          refresh();
        },
        onError: () => notify("Error al actualizar recordatorio", { type: "warning" }),
      }
    );
  };
  console.log("Plan del CRM:", plan);
  const esFreemium = plan === "freemium";
  return (
    <div style={{ height: "calc(100vh - 200px)", overflow: "auto" }}>
      <Datagrid
        bulkActionButtons={false}
        rowClick={esFreemium ? false : "edit"}
        data={allRecords}
        isLoading={isLoading}
        sx={{
          pointerEvents: esFreemium ? "none" : "auto",
          opacity: esFreemium ? 0.5 : 1,
          "& .RaDatagrid-headerCell": {
            maxWidth: "100px",
            whiteSpace: "normal",
          },
          "& .RaDatagrid-cell": {
            textAlign: "center",
          },
        }}
      >
        <TextField source="fullName" label="Nombre Completo" />
        <FunctionField label="Teléfono" render={(record) => <WhatsAppField record={record} />} />
        {/* <TextField source="email" label="Email" /> */}
        <TextField source="gender" label="Género" />
        <FunctionField
          label="Fecha de Nacimiento"
          render={(record) => {
            if (!record.birthdate) return "-";

            const birthdate = moment.utc(record.birthdate);
            const today = moment();
            const thisYearBirthday = moment().month(birthdate.month()).date(birthdate.date());

            if (thisYearBirthday.isBefore(today, "day")) {
              thisYearBirthday.add(1, "year");
            }

            const daysUntil = thisYearBirthday.diff(today, "days");
            const age = today.diff(birthdate, "years");

            let icon = null;
            let tooltip = "";

            if (daysUntil === 0) {
              icon = "🎂";
              tooltip = `¡Feliz cumpleaños! Edad: ${age} años`;
            } else if (daysUntil === 1) {
              icon = "⏳";
              tooltip = `Cumple mañana. Edad: ${age + 1} años`;
            } else if (daysUntil <= 7) {
              icon = "📅";
              tooltip = `Cumple en ${daysUntil} días. Edad: ${age + 1} años`;
            }

            return (
              <span style={{ display: "inline-flex", alignItems: "center" }}>
                {birthdate.format("DD/MM/YYYY")}
                {icon && (
                  <Tooltip title={tooltip} arrow>
                    <span style={{ marginLeft: 6, cursor: "default", fontSize: "1rem" }}>{icon}</span>
                  </Tooltip>
                )}
              </span>
            );
          }}
        />
        <FunctionField
          label="Fecha de Último Servicio"
          sortBy="lastBooking.bookingDate"
          render={(record) => {
            const date = record.lastBooking ? moment(record.lastBooking.bookingDate) : null;
            return (
              <div style={{ maxWidth: "100px", textAlign: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
                {record.lastBooking?.bookingDate ? (
                  <Link href={"#/booking/" + record.lastBooking._id + "/show"}>{date.format("DD/MM/YYYY")}</Link>
                ) : (
                  "-"
                )}
              </div>
            );
          }}
        />
        <FunctionField
          label="Dias desde el ultimo servicio"
          sortBy="daysSinceLastService"
          render={(record) => {
            return (
              <div style={{ maxWidth: "100px", textAlign: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
                {record.lastBooking?.bookingDate ? record.daysSinceLastService : "-"}
              </div>
            );
          }}
        />
        <FunctionField
          label="Servicios realizados"
          sortBy="historial"
          render={(record) => {
            historial[record.id] ?? <CircularProgress size={20} />;
            return (
              <div style={{ maxWidth: "100px", textAlign: "center", overflow: "hidden", whiteSpace: "nowrap" }}>
                {esFreemium ? 8 : (historial[record.id] ?? <CircularProgress size={20} />)}
              </div>
            );
          }}
        />
        <FunctionField label="Historial" render={(record) => <ServicesButton record={record} />} />
        <FunctionField label="Formularios" render={(record) => <FormsButton record={record} />} />
        <FunctionField
          label="Detalle"
          render={(record) => (
            <MuiTextField
              variant="outlined"
              size="small"
              fullWidth
              value={details[record.id] !== undefined ? details[record.id] : record.detail || ""}
              onChange={(e) => handleDetailChange(record.id, e.target.value)}
              onBlur={() => handleDetailBlur(record)}
              onClick={(e) => e.stopPropagation()}
              placeholder="Agregar detalle"
            />
          )}
        />
        {isRecordatorio && (
          <FunctionField
            label="Recordatorio"
            render={(record) => {
              return (
                <span style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "0.5em" }}>
                  {/* record.reminderEnabled ? 'Sí' : 'No' */}
                  <Checkbox
                    checked={Boolean(record.reminderEnabled)}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleUpdateReminder(e, record);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    disabled={isLoading}
                    sx={{ color: "#FF770D" }}
                  />
                </span>
              );
            }}
          />
        )}
      </Datagrid>

      {isLoading && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <Loading />
        </div>
      )}

      {!isLoading && !canLoadMore && (
        <div style={{ textAlign: "center", marginTop: 20 }}>
          <p>Todos los clientes han sido cargados.</p>
        </div>
      )}

      {canLoadMore && !isLoading && hasNextPage && (
        <div ref={sentinelRef} style={{ border: "1px solid transparent", padding: 0, marginTop: 0 }} />
      )}

      {canLoadMore && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            marginTop: 20,
            marginBottom: 20,
          }}
        >
          <CircularProgress />
        </div>
      )}
    </div>
  );
};

export default InfiniteListLayout;
